import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { useTimer, formatHMS } from '../contexts/TimerContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { handleStructuredPaste } from '@/lib/pasteUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Play, Pause, Square, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const authHeader = () => {
  const t = localStorage.getItem('uh_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const TimerPage = () => {
  const { user } = useAuth();
  const { timer, liveElapsedSeconds, tick, start, pause, resume, stop, discard, updateActive, isActive } = useTimer();
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [descDirty, setDescDirty] = useState(false);

  // Load jobs once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/jobs`, { headers: authHeader() });
        setJobs((data || []).filter((j) => j.status !== 'completed' && j.status !== 'archived'));
      } catch (e) {
        toast.error('Failed to load jobs');
      }
    })();
  }, []);

  // When an active timer exists, sync local state from it
  useEffect(() => {
    if (timer) {
      setSelectedJobId(timer.job_id || '');
      if (!descDirty) setDescription(timer.description || '');
    }
  }, [timer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save description after typing pause (debounced)
  useEffect(() => {
    if (!isActive || !descDirty) return;
    const id = setTimeout(async () => {
      try {
        await updateActive({ description });
        setDescDirty(false);
      } catch (e) { /* ignore */ }
    }, 800);
    return () => clearTimeout(id);
  }, [description, descDirty, isActive, updateActive]);

  const handleStart = async () => {
    if (!selectedJobId) {
      toast.error('Pick a job first');
      return;
    }
    setSubmitting(true);
    try {
      await start(selectedJobId, description);
      toast.success('Timer started');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start timer');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePauseResume = async () => {
    setSubmitting(true);
    try {
      if (timer.status === 'running') {
        await pause();
        toast.message('Paused');
      } else {
        await resume();
        toast.message('Resumed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStop = async () => {
    setSubmitting(true);
    try {
      const result = await stop(description);
      if (result.discarded) {
        toast.message('Timer was under 1 minute — nothing logged');
      } else {
        toast.success(`Logged ${result.hours} hours`);
      }
      setDescription('');
      setDescDirty(false);
      navigate('/time-tracking');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not stop timer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDiscard = async () => {
    if (!window.confirm('Discard this timer without logging?')) return;
    try {
      await discard();
      setDescription('');
      setDescDirty(false);
      toast.message('Timer discarded');
    } catch (e) {
      toast.error('Could not discard');
    }
  };

  const elapsed = liveElapsedSeconds;
  // tick keeps display fresh
  void tick;

  const activeJobLabel = (() => {
    if (timer?.job_number) return `${timer.job_number} ${timer.job_title ? '— ' + timer.job_title : ''}`;
    const j = jobs.find((x) => x.id === selectedJobId);
    return j ? `${j.job_number} — ${j.title}` : '';
  })();

  const statusColor = !isActive ? '#94a3b8' : timer.status === 'running' ? '#10b981' : '#f59e0b';
  const statusLabel = !isActive ? 'Ready' : timer.status === 'running' ? 'Running' : 'Paused';

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-slate-50 to-slate-100 -mx-4 -my-6 sm:-mx-6 sm:-my-8 px-4 py-6 sm:px-6 sm:py-8">
      <div className="max-w-md mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900">Time Tracker</h1>
          <p className="text-sm text-slate-500 mt-1">Don't leave money on the table</p>
        </div>

        {/* Big timer display */}
        <Card className="p-8 text-center shadow-lg border-0" data-testid="timer-display-card">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{
                backgroundColor: statusColor,
                animation: isActive && timer.status === 'running' ? 'pulse 2s infinite' : 'none',
              }}
            />
            <span className="text-xs font-medium uppercase tracking-wider text-slate-500" data-testid="timer-status">
              {statusLabel}
            </span>
          </div>
          <div
            className="font-mono text-5xl sm:text-6xl font-bold tracking-tight"
            style={{ color: '#1F3C4F', fontVariantNumeric: 'tabular-nums' }}
            data-testid="timer-elapsed"
          >
            {formatHMS(elapsed)}
          </div>
          {isActive && activeJobLabel && (
            <p className="text-sm text-slate-600 mt-3 truncate" data-testid="timer-active-job">
              {activeJobLabel}
            </p>
          )}
        </Card>

        {/* Job picker (disabled while active) */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Job</label>
          <Select
            value={selectedJobId}
            onValueChange={(v) => {
              setSelectedJobId(v);
              if (isActive && v !== timer.job_id) updateActive({ job_id: v }).catch(() => {});
            }}
            disabled={submitting}
          >
            <SelectTrigger className="h-12 text-base" data-testid="timer-job-select">
              <SelectValue placeholder="Select a job to track" />
            </SelectTrigger>
            <SelectContent>
              {jobs.map((j) => (
                <SelectItem key={j.id} value={j.id}>
                  {j.job_number} — {j.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Description (live notes) */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">
            Notes {isActive && <span className="text-xs text-slate-400">· auto-saves</span>}
          </label>
          <Textarea
            value={description}
            onChange={(e) => { setDescription(e.target.value); setDescDirty(true); }}
            onPaste={handleStructuredPaste}
            placeholder="What are you working on?"
            rows={4}
            className="text-base resize-none"
            data-testid="timer-description"
          />
        </div>

        {/* Action buttons — big, thumb-friendly */}
        {!isActive ? (
          <Button
            onClick={handleStart}
            disabled={submitting || !selectedJobId}
            className="w-full h-16 text-lg font-semibold shadow-md"
            style={{ backgroundColor: '#10b981', color: 'white' }}
            data-testid="timer-start-btn"
          >
            <Play className="w-6 h-6 mr-2" fill="currentColor" />
            Start Timer
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handlePauseResume}
                disabled={submitting}
                className="h-16 text-lg font-semibold shadow-md"
                style={{
                  backgroundColor: timer.status === 'running' ? '#f59e0b' : '#10b981',
                  color: 'white',
                }}
                data-testid="timer-pause-resume-btn"
              >
                {timer.status === 'running' ? (
                  <><Pause className="w-6 h-6 mr-2" fill="currentColor" /> Pause</>
                ) : (
                  <><Play className="w-6 h-6 mr-2" fill="currentColor" /> Resume</>
                )}
              </Button>
              <Button
                onClick={handleStop}
                disabled={submitting}
                className="h-16 text-lg font-semibold shadow-md"
                style={{ backgroundColor: '#1F3C4F', color: 'white' }}
                data-testid="timer-stop-btn"
              >
                <Square className="w-6 h-6 mr-2" fill="currentColor" /> Stop &amp; Log
              </Button>
            </div>
            <Button
              onClick={handleDiscard}
              variant="ghost"
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="timer-discard-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Discard without logging
            </Button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default TimerPage;
